import { useState, useEffect } from 'react';
import { login as loginApi, logout as logoutApi, getCurrentUser, isAuthenticated } from '@/lib/apiService';
import type { AuthResponse, LoginCredentials, LoginResponse, User } from '@/types/auth';
import { clearStoredAuthSession, USER_PROFILE_UPDATED_EVENT } from '@/lib/api/auth';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => void;
  clearAuth: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(() => getCurrentUser() as User | null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing authentication on mount
    const checkAuth = () => {
      const currentUser = getCurrentUser() as User | null;
      const authenticated = isAuthenticated();
      
      if (authenticated && currentUser) {
        setUser(currentUser);
      }
      
      setIsLoading(false);
    };

    checkAuth();

    window.addEventListener(USER_PROFILE_UPDATED_EVENT, checkAuth);
    return () => {
      window.removeEventListener(USER_PROFILE_UPDATED_EVENT, checkAuth);
    };
  }, []);

  const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await loginApi(credentials);
      if (!('requiresMfa' in response && response.requiresMfa)) {
        setUser((response as AuthResponse).user);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    logoutApi();
    setUser(null);
  };

  const clearAuth = () => {
    clearStoredAuthSession();
    setUser(null);
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    clearAuth,
  };
};
